import { getCanonicalLocale } from '@nextcloud/l10n';
import { getCurrentUser } from '@nextcloud/auth';
import { getLoggerBuilder } from '@nextcloud/logger';
import { basename, extname, dirname } from 'path';

/**
 * @copyright 2019 Christoph Wurst <christoph@winzerhof-wurst.at>
 *
 * @author Christoph Wurst <christoph@winzerhof-wurst.at>
 * @author John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @license AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 *
 */
const humanList = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
const humanListBinary = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
/**
 * Format a file size in a human-like format. e.g. 42GB
 *
 * @param size in bytes
 * @param skipSmallSizes avoid rendering tiny sizes and return '< 1 KB' instead
 */
function formatFileSize(size, skipSmallSizes = false, binaryPrefixes = false) {
    if (typeof size === 'string') {
        size = Number(size);
    }
    /*
    * @note This block previously used Log base 1024, per IEC 80000-13;
    * however, the wrong prefix was used. Now we use decimal calculation
    * with base 1000 per the SI. Base 1024 calculation with binary
    * prefixes is optional, but has yet to be added to the UI.
    */
    // Calculate Log with base 1024 or 1000: size = base ** order
    let order = size > 0 ? Math.floor(Math.log(size) / Math.log(binaryPrefixes ? 1024 : 1000)) : 0;
    // Stay in range of the byte sizes that are defined
    order = Math.min((binaryPrefixes ? humanListBinary.length : humanList.length) - 1, order);
    const readableFormat = binaryPrefixes ? humanListBinary[order] : humanList[order];
    let relativeSize = (size / Math.pow(binaryPrefixes ? 1024 : 1000, order)).toFixed(1);
    if (skipSmallSizes === true && order === 0) {
        return (relativeSize !== '0.0' ? '< 1 ' : '0 ') + (binaryPrefixes ? humanListBinary[1] : humanList[1]);
    }
    if (order < 2) {
        relativeSize = parseFloat(relativeSize).toFixed(0);
    }
    else {
        relativeSize = parseFloat(relativeSize).toLocaleString(getCanonicalLocale());
    }
    return relativeSize + ' ' + readableFormat;
}

/**
 * @copyright 2019 Christoph Wurst <christoph@winzerhof-wurst.at>
 *
 * @author Christoph Wurst <christoph@winzerhof-wurst.at>
 *
 * @license AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 *
 */
const getLogger = user => {
    if (user === null) {
        return getLoggerBuilder()
            .setApp('files')
            .build();
    }
    return getLoggerBuilder()
        .setApp('files')
        .setUid(user.uid)
        .build();
};
var logger = getLogger(getCurrentUser());

/**
 * @copyright Copyright (c) 2021 John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @author John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @license AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 *
 */
class NewFileMenu {
    _entries = [];
    registerEntry(entry) {
        this.validateEntry(entry);
        this._entries.push(entry);
    }
    unregisterEntry(entry) {
        const entryIndex = typeof entry === 'string'
            ? this.getEntryIndex(entry)
            : this.getEntryIndex(entry.id);
        if (entryIndex === -1) {
            logger.warn('Entry not found, nothing removed', { entry, entries: this.getEntries() });
            return;
        }
        this._entries.splice(entryIndex, 1);
    }
    /**
     * Get the list of registered entries
     *
     * @param {FileInfo} context the creation context. Usually the current folder FileInfo
     */
    getEntries(context) {
        if (context) {
            return this._entries
                .filter(entry => typeof entry.if === 'function' ? entry.if(context) : true);
        }
        return this._entries;
    }
    getEntryIndex(id) {
        return this._entries.findIndex(entry => entry.id === id);
    }
    validateEntry(entry) {
        if (!entry.id || !entry.displayName || !(entry.iconSvgInline || entry.iconClass)) {
            throw new Error('Invalid entry');
        }
        if (typeof entry.id !== 'string'
            || typeof entry.displayName !== 'string') {
            throw new Error('Invalid id or displayName property');
        }
        if ((entry.iconClass && typeof entry.iconClass !== 'string')
            || (entry.iconSvgInline && typeof entry.iconSvgInline !== 'string')) {
            throw new Error('Invalid icon provided');
        }
        if (entry.if !== undefined && typeof entry.if !== 'function') {
            throw new Error('Invalid if property');
        }
        if (entry.templateName && typeof entry.templateName !== 'string') {
            throw new Error('Invalid templateName property');
        }
        if (entry.handler && typeof entry.handler !== 'function') {
            throw new Error('Invalid handler property');
        }
        if (!entry.templateName && !entry.handler) {
            throw new Error('At least a templateName or a handler must be provided');
        }
        if (this.getEntryIndex(entry.id) !== -1) {
            throw new Error('Duplicate entry');
        }
    }
}
const getNewFileMenu = function () {
    if (typeof window._nc_newfilemenu === 'undefined') {
        window._nc_newfilemenu = new NewFileMenu();
        logger.debug('NewFileMenu initialized');
    }
    return window._nc_newfilemenu;
};

/**
 * @copyright Copyright (c) 2022 John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @author John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @license AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 *
 */
var FileType;
(function (FileType) {
    FileType["Folder"] = "folder";
    FileType["File"] = "file";
})(FileType || (FileType = {}));

/**
 * @copyright Copyright (c) 2022 John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @author John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @license AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 *
 */
var Permission;
(function (Permission) {
    Permission[Permission["NONE"] = 0] = "NONE";
    Permission[Permission["CREATE"] = 4] = "CREATE";
    Permission[Permission["READ"] = 1] = "READ";
    Permission[Permission["UPDATE"] = 2] = "UPDATE";
    Permission[Permission["DELETE"] = 8] = "DELETE";
    Permission[Permission["SHARE"] = 16] = "SHARE";
    Permission[Permission["ALL"] = 31] = "ALL";
})(Permission || (Permission = {}));
/**
 * Parse the webdav permission string to a permission enum
 * @see https://github.com/nextcloud/server/blob/71f698649f578db19a22457cb9d420fb62c10382/lib/public/Files/DavUtil.php#L58-L88
 */
const parseWebdavPermissions = function (permString = '') {
    let permissions = Permission.NONE;
    if (!permString)
        return permissions;
    if (permString.includes('C') || permString.includes('K'))
        permissions |= Permission.CREATE;
    if (permString.includes('G'))
        permissions |= Permission.READ;
    if (permString.includes('W') || permString.includes('N') || permString.includes('V'))
        permissions |= Permission.UPDATE;
    if (permString.includes('D'))
        permissions |= Permission.DELETE;
    if (permString.includes('R'))
        permissions |= Permission.SHARE;
    return permissions;
};

/**
 * @copyright Copyright (c) 2022 John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @author John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @license AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 *
 */
/**
 * Validate Node construct data
 */
const validateData = (data) => {
    if ('id' in data && (typeof data.id !== 'number' || data.id < 0)) {
        throw new Error('Invalid id type of value');
    }
    if (!data.source) {
        throw new Error('Missing mandatory source');
    }
    if (!data.source.startsWith('http')) {
        throw new Error('Invalid source format');
    }
    if ('mtime' in data && !(data.mtime instanceof Date)) {
        throw new Error('Invalid mtime type');
    }
    if ('crtime' in data && !(data.crtime instanceof Date)) {
        throw new Error('Invalid crtime type');
    }
    if (!data.mime || typeof data.mime !== 'string'
        || !data.mime.match(/^[-\w.]+\/[-+\w.]+$/gi)) {
        throw new Error('Missing or invalid mandatory mime');
    }
    if ('size' in data && typeof data.size !== 'number') {
        throw new Error('Invalid size type');
    }
    if ('permissions' in data && !(typeof data.permissions === 'number'
        && data.permissions >= Permission.NONE
        && data.permissions <= Permission.ALL)) {
        throw new Error('Invalid permissions');
    }
    if ('owner' in data
        && data.owner !== null
        && typeof data.owner !== 'string') {
        throw new Error('Invalid owner type');
    }
    if ('attributes' in data && typeof data.attributes !== 'object') {
        throw new Error('Invalid attributes format');
    }
    if ('root' in data && typeof data.root !== 'string') {
        throw new Error('Invalid root format');
    }
    if (data.root && !data.root.startsWith('/')) {
        throw new Error('Root must start with a leading slash');
    }
};

/**
 * @copyright Copyright (c) 2022 John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @author John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @license AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 *
 */
class Node {
    _data;
    _attributes;
    _knownDavService = /(remote|public)\.php\/(web)?dav/i;
    constructor(data, davService) {
        // Validate data
        validateData(data);
        this._data = data;
        this._attributes = data.attributes || {};
        delete this._data.attributes;
        if (davService) {
            this._knownDavService = davService;
        }
    }
    /**
     * Get the source url to this object
     */
    get source() {
        // strip any ending slash
        return this._data.source.replace(/\/$/i, '');
    }
    /**
     * Get this object name
     */
    get basename() {
        return basename(this.source);
    }
    /**
     * Get this object's extension
     */
    get extension() {
        return extname(this.source);
    }
    /**
     * Get the directory path leading to this object
     * Will use the relative path to root if available
     */
    get dirname() {
        if (this.root) {
            return dirname(this.source.split(this.root).pop() || '/');
        }
        return dirname(this.source);
    }
    /**
     * Get the file mime
     */
    get mime() {
        return this._data.mime;
    }
    /**
     * Get the file size
     */
    get size() {
        return this._data.size;
    }
    /**
     * Get the file attribute
     */
    get attributes() {
        return this._attributes;
    }
    /**
     * Get the file permissions
     */
    get permissions() {
        // If this is not a dav ressource, we can only read it
        if (this.owner === null && !this.isDavRessource) {
            return Permission.READ;
        }
        return this._data.permissions || Permission.READ;
    }
    /**
     * Get the file owner
     */
    get owner() {
        // Remote ressources have no owner
        if (!this.isDavRessource) {
            return null;
        }
        return this._data.owner;
    }
    /**
     * Is this a dav-related ressource ?
     */
    get isDavRessource() {
        return this.source.match(this._knownDavService) !== null;
    }
    /**
     * Get the dav root of this object
     */
    get root() {
        // If provided (recommended), use the root and strip away the ending slash
        if (this._data.root) {
            return this._data.root.replace(/^(.+)\/$/, '$1');
        }
        // Use the source to get the root from the dav service
        if (this.isDavRessource) {
            const root = dirname(this.source);
            return root.split(this._knownDavService).pop() || null;
        }
        return null;
    }
    /**
     * Get the absolute path of this object relative to the root
     */
    get path() {
        return (this.dirname + '/' + this.basename).replace(/\/\//g, '/');
    }
    /**
     * Get the file id if defined in attributes
     */
    get fileid() {
        return this.attributes?.fileid;
    }
    /**
     * Move the node to a new destination
     *
     * @param {string} destination the new source.
     * e.g. https://cloud.domain.com/remote.php/dav/files/emma/Photos/picture.jpg
     */
    move(destination) {
        this._data.source = destination;
    }
    /**
     * Rename the node
     * This aliases the move method for easier usage
     */
    rename(basename) {
        if (basename.includes('/')) {
            throw new Error('Invalid basename');
        }
        this.move(dirname(this.source) + '/' + basename);
    }
}

/**
 * @copyright Copyright (c) 2022 John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @author John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @license AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 *
 */
class File extends Node {
    get type() {
        return FileType.File;
    }
}

/**
 * @copyright Copyright (c) 2022 John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @author John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @license AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 *
 */
class Folder extends Node {
    constructor(data) {
        // enforcing mimes
        super({
            ...data,
            mime: 'httpd/unix-directory'
        });
    }
    get type() {
        return FileType.Folder;
    }
    get extension() {
        return null;
    }
    get mime() {
        return 'httpd/unix-directory';
    }
}

/**
 * @copyright Copyright (c) 2021 John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @author John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @license AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 *
 */
class FileAction {
    _action;
    constructor(action) {
        this.validateAction(action);
        this._action = action;
    }
    get id() {
        return this._action.id;
    }
    get displayName() {
        return this._action.displayName;
    }
    get iconSvgInline() {
        return this._action.iconSvgInline;
    }
    get enabled() {
        return this._action.enabled;
    }
    get exec() {
        return this._action.exec;
    }
    get execBatch() {
        return this._action.execBatch;
    }
    get order() {
        return this._action.order;
    }
    get default() {
        return this._action.default;
    }
    get inline() {
        return this._action.inline;
    }
    get renderInline() {
        return this._action.renderInline;
    }
    validateAction(action) {
        if (!action.id || typeof action.id !== 'string') {
            throw new Error('Invalid id');
        }
        if (!action.displayName || typeof action.displayName !== 'function') {
            throw new Error('Invalid displayName function');
        }
        if (!action.iconSvgInline || typeof action.iconSvgInline !== 'function') {
            throw new Error('Invalid iconSvgInline function');
        }
        if (!action.exec || typeof action.exec !== 'function') {
            throw new Error('Invalid exec function');
        }
        // Optional properties --------------------------------------------
        if ('enabled' in action && typeof action.enabled !== 'function') {
            throw new Error('Invalid enabled function');
        }
        if ('execBatch' in action && typeof action.execBatch !== 'function') {
            throw new Error('Invalid execBatch function');
        }
        if ('order' in action && typeof action.order !== 'number') {
            throw new Error('Invalid order');
        }
        if ('default' in action && typeof action.default !== 'boolean') {
            throw new Error('Invalid default');
        }
        if ('inline' in action && typeof action.inline !== 'function') {
            throw new Error('Invalid inline function');
        }
        if ('renderInline' in action && typeof action.renderInline !== 'function') {
            throw new Error('Invalid renderInline function');
        }
    }
}
const registerFileAction = function (action) {
    if (typeof window._nc_fileactions === 'undefined') {
        window._nc_fileactions = [];
        logger.debug('FileActions initialized');
    }
    // Check duplicates
    if (window._nc_fileactions.find(search => search.id === action.id)) {
        logger.error(`FileAction ${action.id} already registered`, { action });
        return;
    }
    window._nc_fileactions.push(action);
};
const getFileActions = function () {
    return window._nc_fileactions || [];
};

/**
 * @copyright 2019 Christoph Wurst <christoph@winzerhof-wurst.at>
 *
 * @author Christoph Wurst <christoph@winzerhof-wurst.at>
 * @author John Molakvoæ <skjnldsv@protonmail.com>
 *
 * @license AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 *
 */
/**
 * Add a new menu entry to the upload manager menu
 */
const addNewFileMenuEntry = function (entry) {
    const newFileMenu = getNewFileMenu();
    return newFileMenu.registerEntry(entry);
};
/**
 * Remove a previously registered entry from the upload menu
 */
const removeNewFileMenuEntry = function (entry) {
    const newFileMenu = getNewFileMenu();
    return newFileMenu.unregisterEntry(entry);
};
/**
 * Get the list of registered entries from the upload menu
 *
 * @param {FileInfo} context the creation context. Usually the current folder FileInfo
 */
const getNewFileMenuEntries = function (context) {
    const newFileMenu = getNewFileMenu();
    return newFileMenu.getEntries(context);
};

export { File, FileAction, FileType, Folder, Node, Permission, addNewFileMenuEntry, formatFileSize, getFileActions, getNewFileMenuEntries, parseWebdavPermissions, registerFileAction, removeNewFileMenuEntry };
//# sourceMappingURL=index.esm.js.map
